/**
 * SubmissionSuccess Component
 * Displays confirmation page after successful event submission
 * Features:
 * - Displays summary of submitted event details from localStorage
 * - Shows inspiration content (uploaded photo and links)
 * - Renders contact information in a readable format
 * - Uses Lucide icons for visual hierarchy
 * - Formats dates and times with dayjs
 * - Responsive layout with Tailwind CSS
 * - Handles missing/undefined values gracefully
 * - Validates and formats external URLs
 * - Animates page entry with Framer Motion
 * - Logs component lifecycle for debugging
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Users, DollarSign, Star, Phone, Image, Mail, Clock, User, 
  PartyPopper, CheckCircle2, Wine, UtensilsCrossed,
  MapPin
} from 'lucide-react';
import dayjs from 'dayjs';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import logger from '../utils/logger';
import { PageSEO } from '../components/SEO/PageSEO';
import useAnalytics from '../hooks/useAnalytics';
import { useEventForm } from '../context/EventFormContext';

const humanReadable = (value) => {
  if (!value) return 'Not specified';
  if (typeof value === 'string' && value.includes('hour')) return value;
  
  // Handle array values (for location)
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : 'Not specified';
  }
  
  return value.charAt(0).toUpperCase() + value.slice(1);
};

// New function to render inspiration content
const renderInspirationContent = (inspiration) => {
  const elements = [];
  
  if (inspiration?.blobUrl) {
    elements.push(
      <a 
        key="photo"
        href={inspiration.blobUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-fuchsia-600 hover:text-fuchsia-800 hover:underline"
      >
        Uploaded Photo
      </a>
    );
  }

  if (inspiration?.inspirationLink) {
    let url = inspiration.inspirationLink;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    elements.push(
      <a 
        key="link"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-fuchsia-600 hover:text-fuchsia-800 hover:underline"
      >
        {new URL(url).hostname}
      </a>
    );
  }

  if (elements.length === 0) {
    return 'Not provided';
  }

  return <div className="flex flex-col gap-2">{elements}</div>;
};

// Add a function to get the final step based on occasion
const getFinalStep = (occasion) => {
  switch (occasion?.toLowerCase()) {
    case 'private-dining':
      return {
        title: "Bon Appétit! 🍽️",
        description: "Get ready for an unforgettable private dining experience"
      };
    case 'drinks':
      return {
        title: "Time to Celebrate! 🍸",
        description: "Get ready for an amazing drinks event"
      };
    default:
      return {
        title: "You're Ready to Go! ✨",
        description: "Get ready to host your event"
      };
  }
};

export default function SubmissionSuccess() {
  const { formData: eventDetails } = useEventForm();
  const { trackEvent } = useAnalytics();
  
  useEffect(() => {
    logger.group('Success Page');
    logger.info('Event details loaded from context');
    logger.groupEnd();

    trackEvent('Form Submission Completed', {
      occasion: eventDetails?.setScene?.occasion || 'Not specified',
      guestCount: eventDetails?.setScene?.guestCount || 'Not specified',
      budget: eventDetails?.setScene?.budget || 'Not specified'
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove the early return check that was causing the issue
  // Instead, provide default values if data is missing
  const eventSummary = {
    setScene: {
      occasion: eventDetails?.setScene?.occasion || 'Not specified',
      activity: eventDetails?.setScene?.activity || 'Not specified',
      guestCount: eventDetails?.setScene?.guestCount || 'Not specified',
      budget: eventDetails?.setScene?.displayBudget || eventDetails?.setScene?.budget || 'Not specified',
      date: eventDetails?.setScene?.date || 'Not specified',
      startTime: eventDetails?.setScene?.startTime || 'Not specified',
      duration: eventDetails?.setScene?.duration || 'Not specified',
      foodOption: eventDetails?.setScene?.foodAndBev?.foodOption || 'Not specified',
      drinkOption: eventDetails?.setScene?.foodAndBev?.drinkOption || 'Not specified',
      location: (() => {
        // Handle multiple possible location formats
        const locationData = eventDetails?.setScene?.location;
        
        // Handle direct array access (if coming from Airtable)
        if (Array.isArray(locationData)) {
          return locationData.length > 0 ? locationData.join(', ') : 'Not specified';
        }
        
        // Handle neighborhoods array in location object (from localStorage)
        if (locationData?.neighborhoods) {
          return locationData.neighborhoods.includes('all-manhattan') 
            ? 'All Manhattan' 
            : (locationData.neighborhoods.length > 0 
                ? locationData.neighborhoods.join(', ') 
                : 'Not specified');
        }
        
        return 'Not specified';
      })()
    },
    contact: {
      firstName: eventDetails?.contact?.firstName || '',
      lastName: eventDetails?.contact?.lastName || '',
      email: eventDetails?.contact?.email || 'Not specified',
      phone: eventDetails?.contact?.phone || 'Not specified'
    },
    inspiration: eventDetails?.inspiration || {}
  };

  // Get the occasion-specific final step message
  const finalStep = getFinalStep(eventSummary.setScene.occasion);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Add PageSEO component with noindex */}
      <PageSEO
        title="Submission Successful | LetsHost"
        description="Your event submission was successful"
        path="/submission-success"
        noindex={true}
      />
      
      <Navbar />
      <div className="flex-grow p-8 pb-[80px]">
        {/* Main container with two columns and divider */}
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-6">
          {/* Left column - Event details */}
          <div className="lg:w-2/3">
            <h1 className="text-[1.35rem] sm:text-[1.5rem] md:text-3xl font-bold mb-8">
              The magic has begun!{' '}
              <motion.span
                animate={{ 
                  rotate: [0, 15, -15, 0],
                  scale: [1, 1.2, 1.2, 1]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
                className="inline-block"
              >
                ✨
              </motion.span>
              {' '}<span className="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-indigo-600 bg-clip-text text-transparent">Submission Received</span>
            </h1>

            <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-indigo-600 px-6 py-4">
                <h3 className="text-xl font-semibold text-white">Your Event Summary</h3>
              </div>
              
              <div className="p-4 grid grid-cols-2 gap-3 text-sm">
                {[
                  { icon: Star, label: "Occasion", value: humanReadable(eventSummary.setScene.occasion) },
                  { icon: PartyPopper, label: "Activity", value: humanReadable(eventSummary.setScene.activity) },
                  { icon: Users, label: "Guest Count", value: humanReadable(eventSummary.setScene.guestCount) },
                  { icon: DollarSign, label: "Budget", value: humanReadable(eventSummary.setScene.budget) },
                  { icon: Calendar, label: "Date", value: humanReadable(eventSummary.setScene.date) },
                  { icon: Clock, label: "Start Time", value: eventSummary.setScene.startTime ? 
                    dayjs(`2024-01-01T${eventSummary.setScene.startTime}`).format('h:mm A') : 'Not specified' },
                  { icon: Clock, label: "Duration", value: eventSummary.setScene.duration },
                  { icon: MapPin, label: "Location", value: humanReadable(eventSummary.setScene.location) },
                  { icon: UtensilsCrossed, label: "Food Service", value: humanReadable(eventSummary.setScene.foodOption) },
                  { icon: Wine, label: "Drink Service", value: humanReadable(eventSummary.setScene.drinkOption) },
                  { icon: User, label: "Name", value: `${eventSummary.contact.firstName} ${eventSummary.contact.lastName}`.trim() || 'Not specified' },
                  { icon: Mail, label: "Email", value: eventSummary.contact.email },
                  { icon: Phone, label: "Phone", value: eventSummary.contact.phone },
                  { 
                    icon: Image, 
                    label: "Inspiration", 
                    value: renderInspirationContent(eventSummary.inspiration)
                  },
                ].map(({ icon: Icon, label, value, fullWidth }) => (
                  <motion.div 
                    key={label}
                    className={`flex items-start p-2 rounded-lg hover:bg-gray-50 ${fullWidth ? 'col-span-2' : ''}`}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 p-1.5 rounded-lg mr-2">
                      <Icon className="w-4 h-4 text-fuchsia-600" />
                    </div>
                    <div className="text-left min-w-0 flex-1">
                      <span className="font-medium text-gray-900">{label}:</span>
                      <div className="text-gray-600 break-words">
                        {value}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column - Status banners */}
          <div className="lg:w-1/3 mt-8 lg:mt-20">
            <div className="space-y-6 sticky top-24">
              {/* Submission Confirmation Banner */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 flex items-center shadow-sm">
                <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-green-800 font-medium">Submission Confirmed</p>
                  <p className="text-green-600 text-sm">Your event details have been received successfully</p>
                </div>
              </div>

              {/* Dynamic message based on occasion */}
              <div className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-lg p-4 flex items-center shadow-sm">
                <PartyPopper className="w-5 h-5 text-violet-600 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-violet-800 font-medium">{finalStep.title}</p>
                  <p className="text-violet-600 text-sm">{finalStep.description}</p>
                </div>
              </div>

              {/* Additional info or next steps could go here */}
              <div className="bg-gradient-to-br from-fuchsia-50 to-pink-50 border border-fuchsia-200 rounded-lg p-4 shadow-sm">
                <h3 className="text-fuchsia-800 font-medium mb-3">What happens next?</h3>
                <ul className="text-fuchsia-700 text-sm space-y-3">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center bg-gradient-to-br from-fuchsia-200 to-violet-200 rounded-full w-5 h-5 text-fuchsia-800 mr-2 flex-shrink-0 shadow-sm">1</span>
                    <span>Our concierge will review your event details</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center bg-gradient-to-br from-fuchsia-200 to-violet-200 rounded-full w-5 h-5 text-fuchsia-800 mr-2 flex-shrink-0 shadow-sm">2</span>
                    <span>We'll contact you within 24 hours to discuss venue options</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center bg-gradient-to-br from-fuchsia-200 to-violet-200 rounded-full w-5 h-5 text-fuchsia-800 mr-2 flex-shrink-0 shadow-sm">3</span>
                    <span>You'll receive custom venue recommendations to choose from</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}